import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearCurrentChitalishte } from "../../store/slices/chitalishtaSlice";
import { selectInformationCardsByChitalishteId } from "../../store/slices/chitalishtaSlice";
import LibrariesSection from "./LibrariesSection";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Grid,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  Tooltip,
  IconButton,
  Pagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Group,
  Phone,
  Email,
  Language,
  LocationOn,
  Groups,
  Business,
  LibraryBooks,
  Home,
  ArrowBack,
  Work,
  People,
  Book,
  Museum,
  TheaterComedy,
  MusicNote,
  Sports,
  EmojiEvents,
  School as SchoolIcon,
  CorporateFare,
  Psychology,
  TrendingUp as TrendingUpIcon,
  BarChart,
  PieChart,
  ShowChart,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chitalishte-tabpanel-${index}`}
      aria-labelledby={`chitalishte-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const YearlyDataTable = ({ informationCards }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 3; // Винаги 3 години, дори на мобилни

  if (!informationCards || informationCards.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          Няма налични данни за информационни карти
        </Typography>
      </Box>
    );
  }

  // Сортираме картите по година в низходящ ред (най-новите първи)
  const sortedCards = [...informationCards].sort((a, b) => b.year - a.year);

  // Изчисляваме общия брой страници
  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);

  // Взимаме картите за текущата страница
  const startIndex = (page - 1) * itemsPerPage;
  const currentCards = sortedCards.slice(startIndex, startIndex + itemsPerPage);
  const yearsToShow = currentCards.map((card) => card.year);

  // Дефинираме всички възможни показатели
  const indicators = [
    {
      key: "totalMembersCount",
      label: "Общ брой членове",
      shortLabel: "Членове",
    },
    { key: "newMembers", label: "Нови членове", shortLabel: "Нови" },
    { key: "employeesCount", label: "Брой служители", shortLabel: "Служители" },
    {
      key: "folkloreFormations",
      label: "Фолклорни формации",
      shortLabel: "Фолклор",
    },
    {
      key: "theatreFormations",
      label: "Театрални формации",
      shortLabel: "Театър",
    },
    { key: "dancingGroups", label: "Танцови групи", shortLabel: "Танци" },
    { key: "vocalGroups", label: "Вокални групи", shortLabel: "Вокал" },
    {
      key: "workshopsClubsArts",
      label: "Клубове по изкуства",
      shortLabel: "Клубове",
    },
    {
      key: "museumCollections",
      label: "Музейни колекции",
      shortLabel: "Музей",
    },
    {
      key: "projectsParticipationLeading",
      label: "Самостоятелни проекти",
      shortLabel: "Собств. проекти",
    },
    {
      key: "projectsParticipationPartner",
      label: "Проекти в сътрудничество",
      shortLabel: "Съвм. проекти",
    },
    {
      key: "participationInTrainings",
      label: "Участия в обучения",
      shortLabel: "Обучения",
    },
    {
      key: "subsidiaryCount",
      label: "Субсидирани бройки",
      shortLabel: "Субсидии",
    },
    {
      key: "employeesWithHigherEducation",
      label: "Специалисти с висше",
      shortLabel: "С висше",
    },
  ];

  // Създаваме обект с данните за всяка година
  const yearDataMap = {};
  sortedCards.forEach((card) => {
    yearDataMap[card.year] = card;
  });

  // Филтрираме показателите, за да включим само тези, които имат поне една стойност
  const availableIndicators = indicators.filter((indicator) =>
    sortedCards.some((card) => card[indicator.key] !== undefined)
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box>
      {availableIndicators.length > 0 ? (
        <>
          {/* Пагинация */}
          {sortedCards.length > itemsPerPage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                flexDirection: isSmallMobile ? "column" : "row",
                gap: isSmallMobile ? 2 : 0,
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  width: isSmallMobile ? "100%" : "auto",
                  textAlign: "center",
                }}
              >
                Показват се години {startIndex + 1}-
                {Math.min(startIndex + itemsPerPage, sortedCards.length)} от{" "}
                {sortedCards.length}
              </Typography>

              {/* Добавете този Box за центриране на Pagination */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: isSmallMobile ? "100%" : "auto",
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isSmallMobile ? "small" : "medium"}
                  siblingCount={isSmallMobile ? 0 : 1}
                />
              </Box>
            </Box>
          )}

          <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              mt: 2,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              size={isMobile ? "small" : "medium"}
              sx={{
                minWidth: isMobile ? 500 : 650,
                "& .MuiTableCell-root": {
                  py: isMobile ? 1 : 1.5,
                  px: isMobile ? 1 : 2,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      minWidth: isMobile ? 120 : 200,
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      bgcolor: "primary.main",
                    }}
                  >
                    {isMobile ? "Показател" : "Показател"}
                  </TableCell>
                  {yearsToShow.map((year) => (
                    <TableCell
                      key={year}
                      align="center"
                      sx={{
                        color: "white",
                        fontWeight: "bold",
                        minWidth: isMobile ? 80 : 120,
                      }}
                    >
                      {year}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {availableIndicators.map((indicator, index) => (
                  <TableRow
                    key={indicator.key}
                    hover
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "background.default" : "action.hover",
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontWeight: "medium",
                        position: "sticky",
                        left: 0,
                        bgcolor: "inherit",
                        zIndex: 1,
                        borderRight: isMobile ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Tooltip title={indicator.label} placement="right">
                        <span>
                          {isMobile ? indicator.shortLabel : indicator.label}
                        </span>
                      </Tooltip>
                    </TableCell>
                    {yearsToShow.map((year) => (
                      <TableCell
                        key={`${indicator.key}-${year}`}
                        align="center"
                        sx={{
                          fontWeight: "medium",
                        }}
                      >
                        {yearDataMap[year] &&
                        yearDataMap[year][indicator.key] !== undefined
                          ? yearDataMap[year][indicator.key]
                          : "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Няма налични статистически данни
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Информационните карти съществуват, но не съдържат статистически
            показатели.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const CulturalActivitiesSection = ({ informationCards }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 3; // Винаги 3 години

  if (!informationCards || informationCards.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          Няма данни за културни дейности
        </Typography>
      </Box>
    );
  }

  // Сортираме картите по година в низходящ ред
  const sortedCards = [...informationCards].sort((a, b) => b.year - a.year);

  // Изчисляваме общия брой страници
  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);

  // Взимаме картите за текущата страница
  const startIndex = (page - 1) * itemsPerPage;
  const currentCards = sortedCards.slice(startIndex, startIndex + itemsPerPage);

  const culturalFields = [
    {
      key: "folkloreFormations",
      label: "Фолклорни формации",
      shortLabel: "Фолклор",
      icon: <MusicNote />,
      color: "#ff6b6b",
      description: "Брой фолклорни състави и групи",
    },
    {
      key: "theatreFormations",
      label: "Театрални формации",
      shortLabel: "Театър",
      icon: <TheaterComedy />,
      color: "#4ecdc4",
      description: "Театрални групи и състави",
    },
    {
      key: "dancingGroups",
      label: "Танцови групи",
      shortLabel: "Танци",
      icon: <Sports />,
      color: "#45b7d1",
      description: "Танцови ансамбли и групи",
    },
    {
      key: "vocalGroups",
      label: "Вокални групи",
      shortLabel: "Вокал",
      icon: <MusicNote />,
      color: "#96ceb4",
      description: "Хорове и вокални формации",
    },
    {
      key: "workshopsClubsArts",
      label: "Клубове по изкуства",
      shortLabel: "Клубове",
      icon: <Book />,
      color: "#feca57",
      description: "Клубове и работилници",
    },
    {
      key: "museumCollections",
      label: "Музейни колекции",
      shortLabel: "Музей",
      icon: <Museum />,
      color: "#ff9ff3",
      description: "Музейни експонати и колекции",
    },
  ];

  // Намираме максималните стойности за всяка категория за нормализация
  const maxValues = {};
  culturalFields.forEach((field) => {
    const values = currentCards.map((card) => card[field.key] || 0);
    maxValues[field.key] = Math.max(...values, 1);
  });

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box>
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Легенда и обща информация */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              textAlign: "center",
            }}
          >
            <Box>
              <Typography variant={isMobile ? "h6" : "h6"} sx={{ mb: 1 }}>
                🎭 Културни дейности - Сравнителен преглед
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {isMobile
                  ? "Показват се данните за последните 3 години"
                  : "Показват се данните за последните 3 години. Височината на колоните визуализира относителния брой на дейностите."}
              </Typography>
            </Box>

            {/* Пагинация - центрирана */}
            {sortedCards.length > itemsPerPage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <IconButton
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  sx={{ color: "white" }}
                  size="small"
                >
                  <NavigateBefore />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ color: "white", minWidth: 40, textAlign: "center" }}
                >
                  {page} / {totalPages}
                </Typography>
                <IconButton
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  sx={{ color: "white" }}
                  size="small"
                >
                  <NavigateNext />
                </IconButton>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Таблица за бърз преглед */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: isMobile ? 2 : 3, height: "100%" }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Преглед" : "Бърз преглед"}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center" }}>Година</TableCell>
                    <TableCell align="center">Общо</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentCards.map((card) => {
                    const totalActivities = culturalFields.reduce(
                      (sum, field) => sum + (card[field.key] || 0),
                      0
                    );
                    return (
                      <TableRow key={card.year}>
                        <TableCell
                          sx={{ fontWeight: "bold", textAlign: "center" }}
                        >
                          {card.year}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={totalActivities}
                            size="small"
                            color={totalActivities > 0 ? "primary" : "default"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Визуализация на данните */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShowChart
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Категории" : "Сравнение по категории"}
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {culturalFields.map((field) => {
                const hasData = currentCards.some(
                  (card) => card[field.key] > 0
                );
                if (!hasData) return null;

                return (
                  <Grid item xs={12} sm={6} lg={4} key={field.key}>
                    <Card
                      sx={{
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 3,
                        },
                        height: "100%",
                        textAlign: "center",
                      }}
                    >
                      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            justifyContent: "center",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: field.color,
                              mr: 2,
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                            }}
                          >
                            {React.cloneElement(field.icon, {
                              fontSize: isMobile ? "small" : "medium",
                            })}
                          </Avatar>
                          <Box>
                            <Typography
                              variant={isMobile ? "subtitle1" : "h6"}
                              sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                            >
                              {isMobile ? field.shortLabel : field.label}
                            </Typography>
                            {!isMobile && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {field.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Grid container spacing={1} justifyContent="center">
                          {currentCards.map((card) => {
                            const value = card[field.key] || 0;
                            const percentage =
                              (value / maxValues[field.key]) * 100;

                            return (
                              <Grid item xs={4} key={card.year}>
                                <Tooltip
                                  title={`${value} ${
                                    isMobile
                                      ? field.shortLabel
                                      : field.label.toLowerCase()
                                  } през ${card.year} г.`}
                                >
                                  <Box sx={{ textAlign: "center" }}>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {card.year}
                                    </Typography>
                                    <Box
                                      sx={{
                                        height: 40,
                                        bgcolor: field.color,
                                        borderRadius: 1,
                                        display: "flex",
                                        alignItems: "flex-end",
                                        justifyContent: "center",
                                        opacity: value > 0 ? 0.8 : 0.3,
                                        background:
                                          value > 0
                                            ? `linear-gradient(to top, ${field.color}, ${field.color}99)`
                                            : "grey.300",
                                        transition: "all 0.3s ease",
                                        mx: "auto",
                                        width: "80%",
                                      }}
                                      style={{
                                        height: `${Math.max(
                                          percentage * 0.4,
                                          8
                                        )}px`,
                                      }}
                                    >
                                      {value > 0 && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "white",
                                            fontWeight: "bold",
                                            textShadow:
                                              "1px 1px 2px rgba(0,0,0,0.5)",
                                            fontSize: isMobile
                                              ? "0.7rem"
                                              : "0.8rem",
                                          }}
                                        >
                                          {value}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </Tooltip>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>

        {/* Детайлна таблица */}
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PieChart
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Детайли" : "Детайлна таблица"}
            </Typography>
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center" }}>
                      Категория
                    </TableCell>
                    {currentCards.map((card) => (
                      <TableCell
                        key={card.year}
                        align="center"
                        sx={{ fontWeight: "bold" }}
                      >
                        {card.year}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {culturalFields.map((field) => (
                    <TableRow key={field.key} hover>
                      <TableCell
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: isMobile ? 120 : 200,
                          justifyContent: isMobile ? "center" : "flex-start",
                        }}
                      >
                        <Box sx={{ color: field.color, mr: 1 }}>
                          {React.cloneElement(field.icon, {
                            fontSize: isMobile ? "small" : "medium",
                          })}
                        </Box>
                        <Typography variant={isMobile ? "body2" : "body1"}>
                          {isMobile ? field.shortLabel : field.label}
                        </Typography>
                      </TableCell>
                      {currentCards.map((card) => (
                        <TableCell
                          key={`${field.key}-${card.year}`}
                          align="center"
                        >
                          <Chip
                            label={card[field.key] || 0}
                            variant={
                              card[field.key] > 0 ? "filled" : "outlined"
                            }
                            color={card[field.key] > 0 ? "primary" : "default"}
                            size={isMobile ? "small" : "medium"}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const ProjectsAndStaffSection = ({ informationCards }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 3; // Винаги 3 години

  if (!informationCards || informationCards.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="h6" color="text.secondary">
          Няма данни за проекти и персонал
        </Typography>
      </Box>
    );
  }

  // Сортираме картите по година в низходящ ред
  const sortedCards = [...informationCards].sort((a, b) => b.year - a.year);

  // Изчисляваме общия брой страници
  const totalPages = Math.ceil(sortedCards.length / itemsPerPage);

  // Взимаме картите за текущата страница
  const startIndex = (page - 1) * itemsPerPage;
  const currentCards = sortedCards.slice(startIndex, startIndex + itemsPerPage);

  const projectFields = [
    {
      key: "projectsParticipationLeading",
      label: "Самостоятелни проекти",
      shortLabel: "Собств. проекти",
      icon: <EmojiEvents />,
      color: "#ff6b6b",
    },
    {
      key: "projectsParticipationPartner",
      label: "Проекти в сътрудничество",
      shortLabel: "Съвм. проекти",
      icon: <Group />,
      color: "#4ecdc4",
    },
    {
      key: "participationInTrainings",
      label: "Участия в обучения",
      shortLabel: "Обучения",
      icon: <SchoolIcon />,
      color: "#45b7d1",
    },
  ];

  const staffFields = [
    {
      key: "employeesCount",
      label: "Общ персонал",
      shortLabel: "Персонал",
      icon: <People />,
      color: "#96ceb4",
    },
    {
      key: "subsidiaryCount",
      label: "Субсидирани бройки",
      shortLabel: "Субсидии",
      icon: <CorporateFare />,
      color: "#feca57",
    },
    {
      key: "employeesWithHigherEducation",
      label: "Специалисти с висше",
      shortLabel: "С висше",
      icon: <Psychology />,
      color: "#ff9ff3",
    },
  ];

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box>
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Хедър */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              mb: 3,
              background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
              color: "white",
              textAlign: "center",
            }}
          >
            <Box>
              <Typography variant={isMobile ? "h6" : "h6"} sx={{ mb: 1 }}>
                📊 Проекти и персонал - Аналитичен преглед
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {isMobile
                  ? "Показват се данните за последните 3 години"
                  : "Цялостна картина на проектната дейност и персонала за последните 3 години"}
              </Typography>
            </Box>

            {/* Пагинация - центрирана */}
            {sortedCards.length > itemsPerPage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <IconButton
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  sx={{ color: "white" }}
                  size="small"
                >
                  <NavigateBefore />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ color: "white", minWidth: 40, textAlign: "center" }}
                >
                  {page} / {totalPages}
                </Typography>
                <IconButton
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  sx={{ color: "white" }}
                  size="small"
                >
                  <NavigateNext />
                </IconButton>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Проекти */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: isMobile ? 2 : 3, height: "100%" }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 3,
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmojiEvents
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Проекти" : "Проектна дейност"}
            </Typography>

            <Grid container spacing={2} justifyContent="center">
              {projectFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.key}>
                  <Card
                    sx={{
                      bgcolor: "background.default",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          flexDirection: "column",
                          textAlign: "center",
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: field.color,
                            mb: 1,
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40,
                          }}
                        >
                          {React.cloneElement(field.icon, {
                            fontSize: isMobile ? "small" : "medium",
                          })}
                        </Avatar>
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                        >
                          {isMobile ? field.shortLabel : field.label}
                        </Typography>
                      </Box>

                      <Grid container spacing={1} justifyContent="center">
                        {currentCards.map((card) => {
                          const value = card[field.key] || 0;
                          return (
                            <Grid item xs={4} key={card.year}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {card.year}
                                </Typography>
                                <Box
                                  sx={{
                                    p: isMobile ? 1 : 1.5,
                                    bgcolor:
                                      value > 0
                                        ? `${field.color}20`
                                        : "grey.50",
                                    borderRadius: 2,
                                    border: `2px solid ${
                                      value > 0 ? field.color : "grey.300"
                                    }`,
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  <Typography
                                    variant={isMobile ? "body2" : "h6"}
                                    sx={{
                                      color:
                                        value > 0 ? field.color : "grey.500",
                                      fontWeight: "bold",
                                      fontSize: isMobile ? "0.8rem" : "1rem",
                                    }}
                                  >
                                    {value}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Персонал */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: isMobile ? 2 : 3, height: "100%" }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 3,
                color: "secondary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <People
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Персонал" : "Персонал"}
            </Typography>

            <Grid container spacing={2} justifyContent="center">
              {staffFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.key}>
                  <Card
                    sx={{
                      bgcolor: "background.default",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          flexDirection: "column",
                          textAlign: "center",
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: field.color,
                            mb: 1,
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40,
                          }}
                        >
                          {React.cloneElement(field.icon, {
                            fontSize: isMobile ? "small" : "medium",
                          })}
                        </Avatar>
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                        >
                          {isMobile ? field.shortLabel : field.label}
                        </Typography>
                      </Box>

                      <Grid container spacing={1} justifyContent="center">
                        {currentCards.map((card) => {
                          const value = card[field.key] || 0;
                          const maxInField = Math.max(
                            ...currentCards.map((c) => c[field.key] || 0),
                            1
                          );
                          const percentage = (value / maxInField) * 100;

                          return (
                            <Grid item xs={4} key={card.year}>
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                >
                                  {card.year}
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                  <Typography
                                    variant={isMobile ? "body2" : "h6"}
                                    sx={{
                                      color: field.color,
                                      fontWeight: "bold",
                                      fontSize: isMobile ? "0.8rem" : "1rem",
                                    }}
                                  >
                                    {value}
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={percentage}
                                  sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    bgcolor: "grey.200",
                                    "& .MuiLinearProgress-bar": {
                                      bgcolor: field.color,
                                    },
                                  }}
                                />
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Сравнителна таблица */}
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrendingUpIcon
                sx={{ mr: 1, fontSize: isMobile ? "1.2rem" : "1.5rem" }}
              />
              {isMobile ? "Сравнение" : "Сравнителен преглед"}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "primary.main", textAlign: "center" }}
                >
                  Проекти
                </Typography>
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: "center" }}>
                          Тип проект
                        </TableCell>
                        {currentCards.map((card) => (
                          <TableCell key={card.year} align="center">
                            {card.year}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {projectFields.map((field) => (
                        <TableRow key={field.key} hover>
                          <TableCell
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: isMobile ? 120 : 200,
                              justifyContent: isMobile
                                ? "center"
                                : "flex-start",
                            }}
                          >
                            <Box sx={{ color: field.color, mr: 1 }}>
                              {React.cloneElement(field.icon, {
                                fontSize: isMobile ? "small" : "medium",
                              })}
                            </Box>
                            <Typography variant={isMobile ? "body2" : "body1"}>
                              {isMobile ? field.shortLabel : field.label}
                            </Typography>
                          </TableCell>
                          {currentCards.map((card) => (
                            <TableCell
                              key={`${field.key}-${card.year}`}
                              align="center"
                            >
                              <Chip
                                label={card[field.key] || 0}
                                variant={
                                  card[field.key] > 0 ? "filled" : "outlined"
                                }
                                color="primary"
                                size={isMobile ? "small" : "medium"}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "secondary.main", textAlign: "center" }}
                >
                  Персонал
                </Typography>
                <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: "center" }}>
                          Категория
                        </TableCell>
                        {currentCards.map((card) => (
                          <TableCell key={card.year} align="center">
                            {card.year}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {staffFields.map((field) => (
                        <TableRow key={field.key} hover>
                          <TableCell
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: isMobile ? 120 : 200,
                              justifyContent: isMobile
                                ? "center"
                                : "flex-start",
                            }}
                          >
                            <Box sx={{ color: field.color, mr: 1 }}>
                              {React.cloneElement(field.icon, {
                                fontSize: isMobile ? "small" : "medium",
                              })}
                            </Box>
                            <Typography variant={isMobile ? "body2" : "body1"}>
                              {isMobile ? field.shortLabel : field.label}
                            </Typography>
                          </TableCell>
                          {currentCards.map((card) => (
                            <TableCell
                              key={`${field.key}-${card.year}`}
                              align="center"
                            >
                              <Chip
                                label={card[field.key] || 0}
                                variant={
                                  card[field.key] > 0 ? "filled" : "outlined"
                                }
                                color="secondary"
                                size={isMobile ? "small" : "medium"}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const ChitalishteDetail = ({ chitalishte }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const informationCards = useSelector((state) =>
    selectInformationCardsByChitalishteId(state, chitalishte?.id)
  );
  const [tabValue, setTabValue] = React.useState(0);

  if (!chitalishte) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="h4" color="text.secondary">
          Читалището не е намерено
        </Typography>
      </Container>
    );
  }

  const handleBack = () => {
    dispatch(clearCurrentChitalishte());
    navigate("/community-center");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const {
    name,
    town,
    municipality,
    region,
    address,
    phone,
    email,
    chitalishtaUrl,
    chairman,
    secretary,
    bulstat,
    status,
    registrationNumber,
  } = chitalishte;

  return (
    <Container
      maxWidth="xl"
      sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}
    >
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "left" }}>
        <Paper
          sx={{
            p: 2,
            background: "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
            color: "white",
            borderRadius: 2,
            boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
            mb: 3,
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            {/* Икона - само на десктоп */}
            {!isMobile && (
              <Grid item>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: "rgba(255,255,255,0.15)",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <Groups sx={{ fontSize: 32, color: "white" }} />
                </Box>
              </Grid>
            )}

            {/* Основна информация */}
            <Grid item xs>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.2,
                    fontSize: isMobile ? "1.1rem" : "1.5rem",
                  }}
                >
                  {name}
                </Typography>
                <Chip
                  label={status === "Закрито" ? "Закрито" : "Действащо"}
                  size="small"
                  sx={{
                    bgcolor:
                      status === "Закрито" ? "error.main" : "success.main",
                    color: "white",
                    height: 20,
                    fontSize: "0.7rem",
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <LocationOn sx={{ fontSize: 16, opacity: 0.8 }} />
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.9, fontSize: "0.875rem" }}
                >
                  {town}, {municipality}, {region}
                </Typography>
              </Box>
            </Grid>

            {/* Контакт бутони - компактни IconButtons с Tooltip */}
            <Grid item>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                {phone && (
                  <Tooltip title={phone}>
                    <IconButton
                      component="a"
                      href={`tel:${phone}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                      }}
                    >
                      <Phone fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {email && (
                  <Tooltip title={email}>
                    <IconButton
                      component="a"
                      href={`mailto:${email}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                      }}
                    >
                      <Email fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {chitalishtaUrl && (
                  <Tooltip title="Уебсайт">
                    <IconButton
                      component="a"
                      href={
                        chitalishtaUrl.startsWith("http")
                          ? chitalishtaUrl
                          : `https://${chitalishtaUrl}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                      }}
                    >
                      <Language fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Назад">
                  <IconButton
                    onClick={handleBack}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                    }}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Information Cards Alert */}
      {informationCards.length === 0 && (
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              background: "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)",
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h6"
              color="warning.dark"
              sx={{ mb: 1, fontSize: isMobile ? "1rem" : "1.25rem" }}
            >
              ℹ️ Информационни карти
            </Typography>
            <Typography
              color="warning.dark"
              variant={isMobile ? "body2" : "body1"}
            >
              За това читалище все още няма качени информационни карти. Данните
              ще станат достъпни след като бъдат добавени от администратора.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Tabs */}
      <Paper
        sx={{
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
            // Центриране за всички устройства
            "& .MuiTabs-flexContainer": {
              justifyContent: isMobile ? "center" : "center",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "primary.main",
            },
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: "#3f51b5",
              height: 3,
            },
          }}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          centered={!isMobile}
        >
          <Tab
            label={isMobile ? "Основна" : "Основна информация"}
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              py: 2,
              minWidth: "auto",
              px: isMobile ? 2 : 3,
            }}
          />
          <Tab
            label={isMobile ? "Статистика" : "Статистика по години"}
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              py: 2,
              minWidth: "auto",
              px: isMobile ? 2 : 3,
            }}
          />
          <Tab
            label={isMobile ? "Култура" : "Културни дейности"}
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              py: 2,
              minWidth: "auto",
              px: isMobile ? 2 : 3,
            }}
          />
          <Tab
            label={isMobile ? "Проекти" : "Проекти и персонал"}
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              py: 2,
              minWidth: "auto",
              px: isMobile ? 2 : 3,
            }}
          />
          <Tab
            label={isMobile ? "Библиотека" : "Библиотека"}
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              py: 2,
              minWidth: "auto",
              px: isMobile ? 2 : 3,
            }}
          />
        </Tabs>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: isMobile ? 1 : 3 }}>
            <Grid container spacing={isMobile ? 2 : 4} justifyContent="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  Контактна информация
                </Typography>
                <Box sx={{ space: 2 }}>
                  {/* Адрес */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      p: 2,
                      backgroundColor: "grey.50",
                      borderRadius: 2,
                      justifyContent: isMobile ? "center" : "flex-start",
                    }}
                  >
                    <LocationOn
                      sx={{
                        mr: 2,
                        color: "primary.main",
                        fontSize: isMobile ? 24 : 28,
                      }}
                    />
                    <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Адрес
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {address || "Няма данни"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Телефон */}
                  {phone && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        justifyContent: isMobile ? "center" : "flex-start",
                      }}
                    >
                      <Phone
                        sx={{
                          mr: 2,
                          color: "secondary.main",
                          fontSize: isMobile ? 24 : 28,
                        }}
                      />
                      <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Телефон
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                          component="a"
                          href={`tel:${phone.replace(/\s/g, "")}`}
                        >
                          {phone}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Имейл */}
                  {email && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        justifyContent: isMobile ? "center" : "flex-start",
                      }}
                    >
                      <Email
                        sx={{
                          mr: 2,
                          color: "warning.main",
                          fontSize: isMobile ? 24 : 28,
                        }}
                      />
                      <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Имейл
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                            wordBreak: "break-word",
                          }}
                          component="a"
                          href={`mailto:${email}`}
                        >
                          {email}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Община */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      p: 2,
                      backgroundColor: "grey.50",
                      borderRadius: 2,
                      justifyContent: isMobile ? "center" : "flex-start",
                    }}
                  >
                    <Business
                      sx={{
                        mr: 2,
                        color: "primary.main",
                        fontSize: isMobile ? 24 : 28,
                      }}
                    />
                    <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Община
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {municipality || "Няма данни"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Област */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 2,
                      backgroundColor: "grey.50",
                      borderRadius: 2,
                      justifyContent: isMobile ? "center" : "flex-start",
                    }}
                  >
                    <Business
                      sx={{
                        mr: 2,
                        color: "primary.main",
                        fontSize: isMobile ? 24 : 28,
                      }}
                    />
                    <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Област
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {region || "Няма данни"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  Ръководство и регистрация
                </Typography>
                <Box sx={{ space: 2 }}>
                  {chairman && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        justifyContent: isMobile ? "center" : "flex-start",
                      }}
                    >
                      <People
                        sx={{
                          mr: 2,
                          color: "secondary.main",
                          fontSize: isMobile ? 24 : 28,
                        }}
                      />
                      <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Председател
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {chairman}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {secretary && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        justifyContent: isMobile ? "center" : "flex-start",
                      }}
                    >
                      <Work
                        sx={{
                          mr: 2,
                          color: "secondary.main",
                          fontSize: isMobile ? 24 : 28,
                        }}
                      />
                      <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Секретар
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {secretary}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {bulstat && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        justifyContent: isMobile ? "center" : "flex-start",
                      }}
                    >
                      <LibraryBooks
                        sx={{
                          mr: 2,
                          color: "secondary.main",
                          fontSize: isMobile ? 24 : 28,
                        }}
                      />
                      <Box sx={{ textAlign: isMobile ? "center" : "left" }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Булстат
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {bulstat}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  Допълнителна информация
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <Chip
                    icon={<Home />}
                    label={`Статус: ${status || "Действащо"}`}
                    variant="filled"
                    color={status === "Закрито" ? "error" : "success"}
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    icon={<People />}
                    label={`Рег. номер: ${registrationNumber || "Няма данни"}`}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Yearly Statistics Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: isMobile ? 1 : 3 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                mb: 4,
                color: "primary.main",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Статистика по години
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              {isMobile
                ? "Показват се данните за последните 3 години"
                : "Показват се данните за последните 3 години. Използвайте пагинацията за достъп до останалите години."}
            </Typography>
            <YearlyDataTable informationCards={informationCards} />
          </Box>
        </TabPanel>

        {/* Cultural Activities Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: isMobile ? 1 : 3 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                mb: 4,
                color: "primary.main",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Културни дейности
            </Typography>
            <CulturalActivitiesSection informationCards={informationCards} />
          </Box>
        </TabPanel>

        {/* Projects and Staff Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ px: isMobile ? 1 : 3 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                mb: 4,
                color: "primary.main",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Проекти и персонал
            </Typography>
            <ProjectsAndStaffSection informationCards={informationCards} />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ px: isMobile ? 1 : 3 }}>
            {/* <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                mb: 4,
                color: "primary.main",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Библиотека
            </Typography> */}
            <LibrariesSection chitalishteId={chitalishte?.id} />
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default ChitalishteDetail;
